import { useState, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Rocket, Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { PLANS, getStripeEnvironment } from "@/lib/stripe";
import { useToast } from "@/hooks/use-toast";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";

// Lazy: Stripe.js (~230 KB) only loads when the checkout dialog opens.
const StripeEmbeddedCheckout = lazy(() =>
  import("@/components/StripeEmbeddedCheckout").then((m) => ({ default: m.StripeEmbeddedCheckout }))
);
import { trackEvent } from "@/lib/meta-pixel";

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  limitReached?: { tier: string; limit: number; used: number } | null;
}

const UpgradeModal = ({ open, onOpenChange, limitReached }: UpgradeModalProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { openCheckout, isOpen: checkoutOpen, options: checkoutOptions, closeCheckout } = useStripeCheckout();
  const [loading, setLoading] = useState<string | null>(null);

  const handleCheckout = (planKey: "monthly" | "annual") => {
    if (!user) return;
    const value = planKey === "monthly" ? 14.99 : 99;
    trackEvent("InitiateCheckout", {
      value,
      currency: "USD",
      content_name: PLANS[planKey].name,
      content_category: "subscription",
    });
    onOpenChange(false);
    openCheckout({
      priceId: PLANS[planKey].priceId,
      quantity: 1,
      customerEmail: user.email,
      userId: user.id,
      returnUrl: `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`,
    });
  };

  const handlePortal = async () => {
    setLoading("portal");
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        body: { returnUrl: window.location.href, environment: getStripeEnvironment() },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      toast({ title: "Error", description: "Could not open subscription management.", variant: "destructive" });
    } finally {
      setLoading(null);
    }
  };

  const isSubscriberLimit = limitReached && limitReached.tier !== "free";
  const isMonthlyLimit = limitReached?.tier === "monthly";
  const isAnnualLimit = limitReached?.tier === "annual";

  return (
    <>
      <Dialog open={checkoutOpen} onOpenChange={(o) => !o && closeCheckout()}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="sr-only">Checkout</DialogTitle>
          {checkoutOptions && (
            <Suspense fallback={<div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>}>
              <StripeEmbeddedCheckout {...checkoutOptions} />
            </Suspense>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md rounded-3xl border-border">
          <DialogHeader className="text-center space-y-3">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto ${isSubscriberLimit ? "bg-gradient-to-br from-sunny to-accent" : "bg-gradient-to-br from-accent to-mint"}`}>
              {isSubscriberLimit ? <AlertTriangle className="w-8 h-8 text-accent-foreground" /> : <Rocket className="w-8 h-8 text-accent-foreground" />}
            </div>
            <DialogTitle className="font-display text-2xl font-800 text-foreground">
              {isAnnualLimit
                ? "Monthly Sessions Used Up! 📚"
                : isMonthlyLimit
                  ? "Monthly Sessions Used Up! 📚"
                  : "Keep the Learning Going! 🚀"}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-base">
              {isAnnualLimit
                ? `You've used all ${limitReached.limit} learning sessions this month. Your sessions will reset at the start of next month!`
                : isMonthlyLimit
                  ? `You've used all ${limitReached.limit} learning sessions this month. Upgrade to Annual for ${PLANS.annual.session_limit} sessions per month!`
                  : "You've used your free trial messages. Upgrade to a paid plan for more learning sessions with your buddy!"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {isAnnualLimit ? (
              <>
                <p className="text-center text-sm text-muted-foreground">
                  Your {limitReached.limit} sessions reset on the 1st of each month.
                </p>
                <Button onClick={handlePortal} disabled={loading === "portal"} variant="outline" className="w-full rounded-full py-6 text-base">
                  {loading === "portal" && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Manage Subscription
                </Button>
              </>
            ) : isMonthlyLimit ? (
              <>
                <Button
                  onClick={() => handleCheckout("annual")}
                  className="w-full rounded-full bg-gradient-to-r from-primary to-secondary text-primary-foreground py-6 text-base shadow-lg hover:shadow-xl transition-all"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Upgrade to Annual — $99/year ({PLANS.annual.session_limit} sessions/mo)
                </Button>
                <Button onClick={handlePortal} disabled={loading === "portal"} variant="outline" className="w-full rounded-full py-6 text-base">
                  {loading === "portal" && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Manage Subscription
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => handleCheckout("monthly")}
                  className="w-full rounded-full bg-gradient-to-r from-primary to-secondary text-primary-foreground py-6 text-base shadow-lg hover:shadow-xl transition-all"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Upgrade — $14.99/month (100 sessions/mo)
                </Button>
                <Button
                  onClick={() => handleCheckout("annual")}
                  variant="outline"
                  className="w-full rounded-full py-6 text-base"
                >
                  Best Value — $99/year (300 sessions/mo, Save 40%)
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpgradeModal;
